if (!customElements.get('variant-style-disclosure')) {
  customElements.define(
    'variant-style-disclosure',
    class VariantStyleDisclosure extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-style-panel]');
        this.values = this.querySelector('[data-style-values]');
        this.toggle = this.querySelector('[data-style-toggle]');
        this.toggleLabel = this.querySelector('[data-style-toggle-label]');
        this.productInfo = this.closest('product-info');

        if (!this.panel || !this.values || !this.toggle || !this.toggleLabel) return;

        this.stateKey = `image-style-${this.dataset.optionPosition || '1'}`;
        if (this.productInfo) {
          this.productInfo.variantStyleDisclosureState ||= new Map();
          this.stateStore = this.productInfo.variantStyleDisclosureState;
        }

        this.expanded = this.stateStore?.get(this.stateKey) ?? false;
        this.abortController = new AbortController();
        this.toggle.addEventListener('click', this.handleToggle, { signal: this.abortController.signal });

        this.desktopMedia = window.matchMedia('(min-width: 750px)');
        this.desktopMedia.addEventListener('change', this.queueRefresh, { signal: this.abortController.signal });

        this.resizeObserver = new ResizeObserver(([entry]) => {
          const width = Math.round(entry.contentRect.width);
          if (width === this.lastObservedWidth) return;

          this.lastObservedWidth = width;
          this.queueRefresh();
        });
        this.resizeObserver.observe(this);
        this.refresh();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.refreshFrame);
        cancelAnimationFrame(this.animationFrame);
        clearTimeout(this.animationTimeout);
        this.animationAbortController?.abort();
      }

      handleToggle = () => {
        if (!this.hasOverflow) return;

        this.expanded = !this.expanded;
        this.stateStore?.set(this.stateKey, this.expanded);
        this.updateToggle();

        if (this.expanded) {
          this.expand();
        } else {
          this.collapse();
        }
      };

      queueRefresh = () => {
        cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = requestAnimationFrame(() => this.refresh());
      };

      refresh() {
        this.cancelAnimation();
        this.items = Array.from(this.querySelectorAll('[data-style-item]'));

        if (!this.items.length || !this.querySelector('.variant-swatch-custom')) {
          this.disableDisclosure();
          return;
        }

        this.classList.add('is-measuring');
        this.revealAllItems();
        this.resetPanelSize();

        const rows = this.groupItemsByRow(this.items);
        this.hasOverflow = rows.length > this.maxCollapsedRows;
        this.toggle.hidden = !this.hasOverflow;

        if (!this.hasOverflow) {
          this.classList.remove('is-collapsed', 'is-expanded');
        } else if (this.expanded) {
          this.classList.remove('is-collapsed');
          this.classList.add('is-expanded');
        } else {
          this.applyCollapsedItems(rows);
          this.classList.remove('is-expanded');
          this.classList.add('is-collapsed');
        }

        this.updateToggle();
        this.classList.remove('is-measuring');
      }

      disableDisclosure() {
        this.hasOverflow = false;
        this.toggle.hidden = true;
        this.classList.remove('is-collapsed', 'is-expanded', 'is-animating', 'is-measuring');
        this.revealAllItems();
        this.resetPanelSize();
        this.updateToggle();
      }

      get maxCollapsedRows() {
        const rawValue = this.desktopMedia.matches ? this.dataset.rowsDesktop : this.dataset.rowsMobile;
        const parsedValue = Number.parseInt(rawValue, 10);

        return Number.isFinite(parsedValue) ? Math.min(6, Math.max(1, parsedValue)) : 2;
      }

      groupItemsByRow(items) {
        return items.reduce((rows, item) => {
          const itemTop = Math.round(item.offsetTop);
          const row = rows.find(({ top }) => Math.abs(top - itemTop) <= 2);

          if (row) {
            row.items.push(item);
          } else {
            rows.push({ top: itemTop, items: [item] });
          }

          return rows;
        }, []);
      }

      applyCollapsedItems(rows = this.groupItemsByRow(this.items)) {
        const visibleItems = new Set(rows.slice(0, this.maxCollapsedRows).flatMap(({ items }) => items));
        const selectedItem = this.items.find((item) => item.querySelector('input[type="radio"]:checked'));

        if (selectedItem && !visibleItems.has(selectedItem)) {
          const lastVisibleItem = Array.from(visibleItems).at(-1);
          if (lastVisibleItem) visibleItems.delete(lastVisibleItem);
          visibleItems.add(selectedItem);
        }

        this.items.forEach((item) => {
          item.hidden = !visibleItems.has(item);
        });

        this.trimOverflowRows(selectedItem);
      }

      trimOverflowRows(selectedItem) {
        let visibleItems = this.items.filter((item) => !item.hidden);
        let visibleRows = this.groupItemsByRow(visibleItems);

        while (visibleRows.length > this.maxCollapsedRows) {
          const removableItem = [...visibleItems].reverse().find((item) => item !== selectedItem);
          if (!removableItem) break;

          removableItem.hidden = true;
          visibleItems = this.items.filter((item) => !item.hidden);
          visibleRows = this.groupItemsByRow(visibleItems);
        }
      }

      revealAllItems() {
        this.items?.forEach((item) => {
          item.hidden = false;
        });
      }

      expand() {
        const startHeight = this.panel.getBoundingClientRect().height;
        this.prepareAnimation(startHeight);
        this.revealAllItems();
        this.classList.remove('is-collapsed');
        this.classList.add('is-expanded');
        this.animatePanel(this.values.scrollHeight);
      }

      collapse() {
        const startHeight = this.panel.getBoundingClientRect().height;
        this.prepareAnimation(startHeight);
        this.applyCollapsedItems();
        this.classList.remove('is-expanded');
        this.classList.add('is-collapsed');
        this.animatePanel(this.values.scrollHeight);
      }

      prepareAnimation(startHeight) {
        this.cancelAnimation();
        this.classList.add('is-animating');
        this.panel.style.height = `${startHeight}px`;
        this.panel.getBoundingClientRect();
      }

      animatePanel(targetHeight) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishAnimation();
          return;
        }

        this.animationFrame = requestAnimationFrame(() => {
          this.panel.style.height = `${targetHeight}px`;
        });

        this.animationAbortController = new AbortController();
        const handleTransitionEnd = (event) => {
          if (event && event.target !== this.panel) return;
          this.finishAnimation();
        };

        this.panel.addEventListener('transitionend', handleTransitionEnd, {
          once: true,
          signal: this.animationAbortController.signal,
        });
        this.animationTimeout = setTimeout(() => this.finishAnimation(), 400);
      }

      finishAnimation() {
        clearTimeout(this.animationTimeout);
        this.animationAbortController?.abort();
        this.animationAbortController = null;
        this.classList.remove('is-animating');
        this.resetPanelSize();
      }

      cancelAnimation() {
        cancelAnimationFrame(this.animationFrame);
        clearTimeout(this.animationTimeout);
        this.animationAbortController?.abort();
        this.animationAbortController = null;
        this.classList.remove('is-animating');
      }

      resetPanelSize() {
        if (!this.panel) return;
        this.panel.style.height = '';
      }

      updateToggle() {
        if (!this.toggle || !this.toggleLabel) return;

        const moreText = this.dataset.moreText?.trim() || 'More styles';
        const lessText = this.dataset.lessText?.trim() || 'Less styles';
        this.toggle.setAttribute('aria-expanded', String(Boolean(this.hasOverflow && this.expanded)));
        this.toggleLabel.textContent = this.expanded ? lessText : moreText;
      }
    }
  );
}
